import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Inbox,
  Ticket,
  Github,
  MessageSquare,
  PenLine,
  Mail,
  Search,
  Wand2,
  Sparkles,
  ArrowRight,
  Slack,
  Check,
  X,
  HelpCircle,
  Send,
  ShieldCheck,
  GitBranch,
  AlertTriangle,
  ListChecks,
  FlaskConical,
  FileCode2,
  Link2,
  CircleDot,
  Loader2,
  User,
  Pencil,
} from "lucide-react";
import { useHive } from "@/store/useHive";
import { APPS } from "@/data/apps";
import { isEnabled } from "@/lib/entitlements";
import {
  TASK_SOURCE_LABEL,
  taskStatusLabel,
  TASK_STATUS_TONE,
  PRIORITY_TONE,
  AUTO_RESOLVE_LEVELS,
} from "@/data/tasks";
import { AppHeader, Badge } from "@/components/ui/primitives";
import { SampleBadge } from "@/components/ui/SampleBadge";
import { Modal } from "@/components/ui/Modal";
import { AutoResolvePanel } from "./task/AutoResolvePanel";
import type { HiveTask, FollowUp } from "@/types";

const accent = APPS.tasks.accent;

const SOURCE_ICON: Record<HiveTask["source"], any> = {
  jira: Ticket,
  github: Github,
  slack: MessageSquare,
  manual: PenLine,
  email: Mail,
};

/* ── score ring ─────────────────────────────────────────── */
function MiniRing({ value, label, color, invert }: { value: number; label: string; color: string; invert?: boolean }) {
  const size = 52;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const shown = invert ? value : value;
  const tone = invert
    ? value > 60
      ? "#f43f5e"
      : value > 35
        ? "#f59e0b"
        : "#22c55e"
    : color;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative grid place-items-center" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" className="text-app/20" strokeWidth={stroke} opacity={0.2} />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={tone}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={{ strokeDashoffset: c - (shown / 100) * c }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            style={{ filter: `drop-shadow(0 0 4px ${tone}88)` }}
          />
        </svg>
        <span className="absolute text-xs font-semibold text-app tabular-nums">{value}</span>
      </div>
      <span className="text-[9px] uppercase tracking-wide text-app-2">{label}</span>
    </div>
  );
}

function TaskRow({ task, index }: { task: HiveTask; index: number }) {
  const selectTask = useHive((s) => s.selectTask);
  const selectedId = useHive((s) => s.selectedTaskId);
  const aresEnabled = useHive((s) => isEnabled(s.entitlements, "ares"));
  const SIcon = SOURCE_ICON[task.source];
  const selected = selectedId === task.id;
  const openQ = task.questions.filter((q) => q.status === "open").length;

  return (
    <motion.button
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.025 }}
      onClick={() => selectTask(task.id)}
      className="w-full rounded-xl border bg-surface/40 p-3 text-left transition"
      style={{
        borderColor: selected ? accent : "hsl(var(--border))",
        boxShadow: selected ? `0 8px 24px -14px ${accent}99` : undefined,
      }}
    >
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface-2 text-app-1">
          <SIcon size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-app-2">{task.id}</span>
            {task.isSample && <SampleBadge />}
            {task.refined && <Sparkles size={11} className="text-emerald-500" />}
          </div>
          <div className="truncate text-sm font-medium text-app">{task.title}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge tone={PRIORITY_TONE[task.priority]}>{task.priority}</Badge>
            <Badge tone={TASK_STATUS_TONE[task.status]}>{taskStatusLabel(task.status, aresEnabled)}</Badge>
            {openQ > 0 && (
              <span className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-500">
                <HelpCircle size={9} /> {openQ}
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] text-app-2">{TASK_SOURCE_LABEL[task.source]}</div>
          <div className="mt-1 flex items-center justify-end gap-1">
            <span className="text-[10px] text-app-2">ready</span>
            <span className="h-1.5 w-10 overflow-hidden rounded-full bg-surface-2">
              <span className="block h-full rounded-full" style={{ width: `${task.scores.readiness}%`, background: task.scores.readiness > 70 ? "#22c55e" : accent }} />
            </span>
          </div>
        </div>
      </div>
    </motion.button>
  );
}

/* ── Slack modal ────────────────────────────────────────── */
function SlackModal({
  task,
  question,
  target,
  onClose,
}: {
  task: HiveTask;
  question: FollowUp;
  target: "reporter" | "assignee";
  onClose: () => void;
}) {
  const askViaSlack = useHive((s) => s.askViaSlack);
  const recipient = target === "reporter" ? task.reporter : task.assignee;
  const channel = `DM · ${recipient}`;
  const [msg, setMsg] = useState(
    `Hey ${recipient.replace(/^@/, "").split(" ")[0]}, Hive is preparing ticket ${task.id} for ARES implementation. ${question.text}`
  );
  const [sent, setSent] = useState(false);

  return (
    <Modal
      open
      onClose={onClose}
      accent="#e01e5a"
      icon={<Slack size={18} />}
      title="Ask via Slack"
      subtitle={`${task.id} · ${recipient}`}
      width={460}
    >
      {sent ? (
        <div className="py-6 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15">
            <Check size={28} className="text-emerald-500" />
          </motion.div>
          <div className="mt-3 text-sm font-medium text-app">Message sent to {recipient}</div>
          <div className="mt-1 text-xs text-app-2">The question is logged and the follow-up marked resolved.</div>
          <button onClick={onClose} className="mt-4 rounded-lg border border-app px-4 py-2 text-xs font-medium text-app-1 hover:border-app-strong">
            Done
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/40 px-3 py-2">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#e01e5a]/15 text-xs font-semibold text-[#e01e5a]">
              {recipient.replace(/^@/, "").slice(0, 2).toUpperCase()}
            </span>
            <div>
              <div className="text-xs font-medium text-app">{recipient}</div>
              <div className="text-[10px] text-app-2">{channel}</div>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-app-2">Message</label>
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              rows={4}
              className="mt-1.5 w-full resize-none rounded-lg border border-app bg-surface px-3 py-2 text-sm text-app outline-none focus:border-app-strong"
            />
          </div>
          <div className="flex items-center justify-between text-[11px] text-app-2">
            <span className="flex items-center gap-1"><ShieldCheck size={11} /> Logged for audit</span>
            <span>via Hive · Slack</span>
          </div>
          <button
            onClick={() => {
              setSent(true);
              askViaSlack(task.id, question.id);
            }}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-white transition hover:brightness-105"
            style={{ background: "#e01e5a" }}
          >
            <Send size={14} /> Send Slack Message
          </button>
        </div>
      )}
    </Modal>
  );
}

/* ── Send to ARES modal ─────────────────────────────────── */
function SendToAresModal({ task, onClose }: { task: HiveTask; onClose: () => void }) {
  const workspaces = useHive((s) => s.codeWorkspaces);
  const sendTaskToAres = useHive((s) => s.sendTaskToAres);
  const setWsWizard = useHive((s) => s.setWsWizard);
  const openApp = useHive((s) => s.openApp);
  const [wsId, setWsId] = useState(task.workspaceId ?? workspaces[0]?.id);
  const [verifyMode, setVerifyMode] = useState("full");
  const [launched, setLaunched] = useState(false);
  const ws = workspaces.find((w) => w.id === wsId);

  return (
    <Modal
      open
      onClose={onClose}
      accent={APPS.ares.accent}
      icon={<ShieldCheck size={18} />}
      title="Send to ARES"
      subtitle={`${task.id} · ${task.title}`}
      width={500}
    >
      {launched ? (
        <div className="py-6 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mx-auto grid h-14 w-14 place-items-center rounded-full" style={{ background: `${APPS.ares.accent}22` }}>
            <ShieldCheck size={28} style={{ color: APPS.ares.accent }} />
          </motion.div>
          <div className="mt-3 text-sm font-medium text-app">Assigned to ARES</div>
          <div className="mt-1 text-xs text-app-2">{task.id} is queued in {ws?.name} on {ws?.branch}.</div>
          <button onClick={onClose} className="mt-4 rounded-lg border border-app px-4 py-2 text-xs font-medium text-app-1 hover:border-app-strong">Done</button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* select workspace (cross-widget) */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-wider text-app-2">Workspace</label>
              <button onClick={() => { onClose(); openApp("workspaces"); setWsWizard(true); }} className="text-[11px] text-app-2 hover:text-app">+ New workspace</button>
            </div>
            <div className="space-y-1.5">
              {workspaces.slice(0, 4).map((w) => (
                <button
                  key={w.id}
                  onClick={() => setWsId(w.id)}
                  className="flex w-full items-center gap-2.5 rounded-lg border p-2.5 text-left transition"
                  style={{ borderColor: wsId === w.id ? APPS.ares.accent : "hsl(var(--border))", background: wsId === w.id ? `${APPS.ares.accent}12` : "transparent" }}
                >
                  <span className="grid h-8 w-8 place-items-center rounded-lg text-white" style={{ background: `linear-gradient(135deg, ${w.accent}, ${w.accent}aa)` }}>
                    <GitBranch size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium text-app">{w.repo}</div>
                    <div className="truncate font-mono text-[10px] text-app-2">{w.branch}</div>
                  </div>
                  {wsId === w.id && <Check size={15} style={{ color: APPS.ares.accent }} />}
                </button>
              ))}
            </div>
          </div>

          {/* confirm branch + verify mode */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-app bg-surface-2/30 p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-app-2">Branch</div>
              <div className="mt-0.5 flex items-center gap-1 font-mono text-xs text-app"><GitBranch size={11} /> {ws?.branch}</div>
            </div>
            <div className="rounded-lg border border-app bg-surface-2/30 p-2.5">
              <div className="text-[10px] uppercase tracking-wide text-app-2">Verification mode</div>
              <select value={verifyMode} onChange={(e) => setVerifyMode(e.target.value)} className="mt-0.5 w-full bg-transparent text-xs font-medium capitalize text-app outline-none">
                <option value="full">Full proof</option>
                <option value="fast">Fast gate</option>
                <option value="property">Property tests</option>
              </select>
            </div>
          </div>

          {/* launch summary */}
          <div className="rounded-xl border border-app bg-surface-2/40 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-app-2">Launch summary</div>
            <div className="mt-2 space-y-1 text-xs text-app-1">
              <div className="flex items-center gap-2"><CircleDot size={11} style={{ color: accent }} /> {task.id} — {task.title}</div>
              <div className="flex items-center gap-2"><GitBranch size={11} className="text-app-2" /> {ws?.repo} · {ws?.branch}</div>
              <div className="flex items-center gap-2"><ShieldCheck size={11} style={{ color: APPS.ares.accent }} /> {verifyMode} verification · readiness {task.scores.readiness}%</div>
            </div>
          </div>

          <button
            onClick={() => { setLaunched(true); sendTaskToAres(task.id, wsId); }}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-black transition hover:brightness-105"
            style={{ background: APPS.ares.accent }}
          >
            <Send size={14} /> Launch ARES implementation
          </button>
        </div>
      )}
    </Modal>
  );
}

/* ── follow-up question row ─────────────────────────────── */
function QuestionRow({ task, q, onSlack }: { task: HiveTask; q: FollowUp; onSlack: (target: "reporter" | "assignee") => void }) {
  const resolveQuestion = useHive((s) => s.resolveQuestion);
  const resolved = q.status !== "open";
  return (
    <div className="rounded-xl border border-app bg-surface-2/30 p-3" style={{ opacity: q.status === "ignored" ? 0.5 : 1 }}>
      <div className="flex items-start gap-2">
        <HelpCircle size={14} className={`mt-0.5 ${resolved ? "text-app-2" : "text-amber-500"}`} />
        <p className="flex-1 text-xs leading-relaxed text-app">{q.text}</p>
        {q.status === "resolved" && <Badge tone="connected">resolved</Badge>}
        {q.status === "ignored" && <Badge tone="queued">ignored</Badge>}
      </div>
      {!resolved && (
        <div className="mt-2.5 flex flex-wrap gap-1.5 pl-6">
          <button onClick={() => resolveQuestion(task.id, q.id, "resolved")} className="rounded-md border border-app px-2 py-1 text-[10px] font-medium text-app-1 transition hover:border-app-strong">Ask user</button>
          <button onClick={() => onSlack("reporter")} className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-white transition hover:brightness-105" style={{ background: "#e01e5a" }}>
            <Slack size={10} /> Ask reporter
          </button>
          <button onClick={() => onSlack("assignee")} className="flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium transition" style={{ borderColor: "#e01e5a55", color: "#e01e5a" }}>
            <Slack size={10} /> Ask assignee
          </button>
          <button onClick={() => resolveQuestion(task.id, q.id, "resolved")} className="rounded-md border border-app px-2 py-1 text-[10px] font-medium text-app-1 transition hover:border-app-strong">Mark resolved</button>
          <button onClick={() => resolveQuestion(task.id, q.id, "ignored")} className="rounded-md px-2 py-1 text-[10px] font-medium text-app-2 transition hover:text-app">Ignore</button>
        </div>
      )}
    </div>
  );
}

function ListBlock({ title, items, icon: Icon, color }: { title: string; items: string[]; icon: any; color?: string }) {
  if (!items.length) return null;
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2">
        <Icon size={12} style={{ color: color ?? "currentColor" }} /> {title}
      </div>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-xs leading-relaxed text-app-1">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full" style={{ background: color ?? accent }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Task detail ────────────────────────────────────────── */
function TaskDetail({ task }: { task: HiveTask }) {
  const refineTask = useHive((s) => s.refineTask);
  const aresEnabled = useHive((s) => isEnabled(s.entitlements, "ares"));
  const openProductPreview = useHive((s) => s.openProductPreview);
  const [refining, setRefining] = useState(false);
  const [slack, setSlack] = useState<{ q: FollowUp; target: "reporter" | "assignee" } | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const SIcon = SOURCE_ICON[task.source];

  function doRefine() {
    setRefining(true);
    setTimeout(() => {
      refineTask(task.id);
      setRefining(false);
    }, 1500);
  }

  const scores = [
    { label: "Clarity", value: task.scores.clarity, color: "#3b82f6" },
    { label: "Scope", value: task.scores.scope, color: "#a855f7" },
    { label: "Impl", value: task.scores.readiness, color: "#22c55e" },
    { label: "Verify", value: task.scores.verification, color: "#06b6d4" },
    { label: "Risk", value: task.scores.risk, color: "#f43f5e", invert: true },
  ];

  return (
    <motion.div key={task.id} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} className="flex h-full flex-col">
      {/* header */}
      <div className="border-b border-app p-4">
        <div className="flex items-start gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-surface-2 text-app-1"><SIcon size={16} /></span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] text-app-2">
              <span className="font-mono">{task.id}</span> · {TASK_SOURCE_LABEL[task.source]}
              {task.isSample && <SampleBadge />}
            </div>
            <div className="text-base font-semibold text-app">{task.title}</div>
          </div>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <Badge tone={PRIORITY_TONE[task.priority]}>{task.priority}</Badge>
          <Badge tone={TASK_STATUS_TONE[task.status]}>{taskStatusLabel(task.status, aresEnabled)}</Badge>
          <span className="flex items-center gap-1 rounded-md bg-surface-2 px-2 py-0.5 text-[11px] text-app-2"><User size={10} /> {task.assignee}</span>
          <span className="rounded-md bg-surface-2 px-2 py-0.5 text-[11px] text-app-2">{task.sla}</span>
        </div>

        {/* readiness scoring */}
        <div className="mt-3 flex items-center justify-between rounded-xl border border-app bg-surface-2/30 px-2 py-2">
          {scores.map((s) => (
            <MiniRing key={s.label} value={s.value} label={s.label} color={s.color} invert={s.invert} />
          ))}
        </div>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto no-scrollbar p-4">
        {/* refine CTA / comparison */}
        {!task.refined ? (
          <div className="rounded-xl border border-app bg-surface-2/30 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-app-2">
                <FileCode2 size={12} /> Original task
              </div>
              <button
                onClick={doRefine}
                disabled={refining}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-black transition hover:brightness-105 disabled:opacity-70"
                style={{ background: accent }}
              >
                {refining ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                {refining ? "Refining…" : "Refine"}
              </button>
            </div>
            <p className="mt-2.5 text-sm leading-relaxed text-app-1">{task.description}</p>
            <AnimatePresence>
              {refining && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 space-y-1.5 overflow-hidden"
                >
                  {["Improving clarity", "Adding acceptance criteria", "Adding verification requirements", "Identifying edge cases"].map((s, i) => (
                    <motion.div key={s} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.28 }} className="flex items-center gap-2 text-xs text-app-2">
                      <Sparkles size={11} style={{ color: accent }} /> {s}…
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2">
              <Sparkles size={12} className="text-emerald-500" /> {aresEnabled ? "Refined for ARES" : "Refined for agents"}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl border border-app bg-surface-2/20 p-3">
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-app-2">Original</div>
                <p className="text-xs leading-relaxed text-app-2">{task.description}</p>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: `${accent}55`, background: `${accent}0d` }}>
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: accent }}>
                  {aresEnabled ? "Refined for ARES" : "Refined"}
                </div>
                <p className="text-xs leading-relaxed text-app">{task.refinedDescription}</p>
              </div>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              <button onClick={() => useHive.getState().pushToast({ title: "Refined version accepted", body: task.id, tone: "success" })} className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105" style={{ background: accent }}>
                <Check size={12} /> Accept
              </button>
              <button onClick={() => useHive.getState().pushToast({ title: "Edit mode", body: "Manual edit drawer (mock)", tone: "info" })} className="flex items-center gap-1 rounded-lg border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
                <Pencil size={12} /> Edit
              </button>
              <button onClick={() => useHive.getState().pushToast({ title: "Posted to Jira", body: `${task.id} updated`, tone: "success" })} className="flex items-center gap-1 rounded-lg border border-app px-2.5 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
                <Ticket size={12} /> Post to Jira
              </button>
            </div>
          </motion.div>
        )}

        {/* acceptance / technical / etc */}
        {task.refined && (
          <>
            <ListBlock title="Acceptance criteria" items={task.acceptance} icon={ListChecks} color="#22c55e" />
            <ListBlock title="Technical context" items={task.technical} icon={FileCode2} color="#3b82f6" />
            <ListBlock title="Suggested implementation plan" items={task.plan} icon={CircleDot} color={accent} />
            <ListBlock title="Verification plan" items={task.verificationPlan} icon={FlaskConical} color="#06b6d4" />
            <ListBlock title="Edge cases" items={task.edgeCases} icon={AlertTriangle} color="#f59e0b" />
          </>
        )}

        {/* linked files + dependencies */}
        <div className="grid grid-cols-2 gap-3">
          <ListBlock title="Linked files" items={task.files} icon={Link2} />
          <ListBlock title="Dependencies" items={task.dependencies} icon={Link2} color="#f43f5e" />
        </div>

        {/* risk */}
        <div className="rounded-xl border border-app bg-surface-2/30 p-3">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2">
            <AlertTriangle size={12} className="text-amber-500" /> Risk assessment
          </div>
          <p className="text-xs leading-relaxed text-app-1">{task.risk}</p>
          <div className="mt-1.5 text-[11px] text-app-2">Suggested owner · <span className="text-app-1">{task.suggestedOwner}</span></div>
        </div>

        {/* follow-up questions */}
        {task.questions.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2">
              <HelpCircle size={12} /> Suggested follow-up questions
              <span className="text-app-2">· {task.questions.filter((q) => q.status === "open").length} open</span>
            </div>
            <div className="space-y-1.5">
              {task.questions.map((q) => (
                <QuestionRow key={q.id} task={task} q={q} onSlack={(target) => setSlack({ q, target })} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* footer — send to ARES (demand capture when the tenant lacks ARES) */}
      <div className="border-t border-app p-3">
        {aresEnabled ? (
          <>
            <button
              onClick={() => setSendOpen(true)}
              disabled={!task.refined || task.status === "blocked"}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-black transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: APPS.ares.accent }}
            >
              <ShieldCheck size={15} /> Send to ARES
              <ArrowRight size={14} />
            </button>
            {!task.refined && <div className="mt-1.5 text-center text-[11px] text-app-2">Refine this task before sending it to ARES.</div>}
            {task.status === "blocked" && <div className="mt-1.5 text-center text-[11px] text-amber-500">Task is blocked — resolve dependencies first.</div>}
          </>
        ) : (
          <>
            <button
              onClick={() => openProductPreview("ares")}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border py-2.5 text-sm font-semibold transition hover:brightness-110"
              style={{ borderColor: APPS.ares.accent, color: APPS.ares.accent, background: `${APPS.ares.accent}14` }}
            >
              <ShieldCheck size={15} /> Requires ARES · Early Access
            </button>
            <div className="mt-1.5 text-center text-[11px] text-app-2">
              ARES agents implement refined tasks autonomously — request early access to turn this on.
            </div>
          </>
        )}
      </div>

      {slack && <SlackModal task={task} question={slack.q} target={slack.target} onClose={() => setSlack(null)} />}
      {sendOpen && <SendToAresModal task={task} onClose={() => setSendOpen(false)} />}
    </motion.div>
  );
}

/* ── empty state ────────────────────────────────────────── */
function EmptyState() {
  const openApp = useHive((s) => s.openApp);
  const aresEnabled = useHive((s) => isEnabled(s.entitlements, "ares"));
  return (
    <div className="grid h-full place-items-center p-10 text-center">
      <div>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mx-auto grid h-20 w-20 place-items-center rounded-3xl text-white shadow-glow" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
          <Inbox size={34} />
        </motion.div>
        <h2 className="mt-5 text-xl font-semibold text-app">Your task queue is clear</h2>
        <p className="mx-auto mt-2 max-w-sm text-sm text-app-2">
          {aresEnabled
            ? "Connect a source so incoming work flows into Hive and gets refined into ARES-ready tasks."
            : "Connect a source so incoming work flows into Hive and gets refined into agent-ready tasks."}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {[["Connect Jira", Ticket], ["Connect GitHub Issues", Github], ["Connect Slack", Slack]].map(([label, Icon]) => (
            <button key={label as string} onClick={() => openApp("integrations")} className="flex items-center gap-1.5 rounded-lg border border-app px-4 py-2.5 text-sm font-medium text-app-1 transition hover:border-app-strong">
              {/* @ts-expect-error icon component */}
              <Icon size={15} /> {label}
            </button>
          ))}
          <button className="flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold text-black" style={{ background: accent }}>
            <PenLine size={15} /> Create manual task
          </button>
        </div>
      </div>
    </div>
  );
}

const SOURCE_FILTERS: (HiveTask["source"] | "all")[] = ["all", "jira", "github", "slack", "manual", "email"];

export function TaskCommandCenterApp() {
  const tasks = useHive((s) => s.tasks);
  const selectedId = useHive((s) => s.selectedTaskId);
  const level = useHive((s) => s.autoResolveLevel);
  const aresEnabled = useHive((s) => isEnabled(s.entitlements, "ares"));
  const [source, setSource] = useState<HiveTask["source"] | "all">("all");
  const [quick, setQuick] = useState<"all" | "ready" | "clarify">("all");
  const [q, setQ] = useState("");
  const [autoOpen, setAutoOpen] = useState(false);

  const filtered = useMemo(
    () =>
      tasks.filter((t) => {
        if (source !== "all" && t.source !== source) return false;
        if (quick === "ready" && t.status !== "ready") return false;
        if (quick === "clarify" && t.status !== "needs-clarification") return false;
        if (q && !`${t.id} ${t.title}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
      }),
    [tasks, source, quick, q]
  );

  const selected = tasks.find((t) => t.id === selectedId);
  const counts = {
    incoming: tasks.filter((t) => t.status !== "done").length,
    ready: tasks.filter((t) => t.status === "ready").length,
    clarify: tasks.filter((t) => t.status === "needs-clarification").length,
    blocked: tasks.filter((t) => t.status === "blocked").length,
  };

  if (tasks.length === 0) return <EmptyState />;

  return (
    <div className="flex h-full flex-col">
      <AppHeader
        icon={Inbox}
        title="Task Command Center"
        subtitle={`${counts.incoming} incoming · ${counts.ready} ready · ${counts.clarify} need clarification · ${counts.blocked} blocked`}
        accent={accent}
        actions={
          <button
            onClick={() => setAutoOpen(true)}
            className="flex items-center gap-1.5 rounded-lg border border-app bg-surface-2/60 px-3 py-1.5 text-xs font-medium text-app-1 transition hover:border-app-strong"
          >
            <Sparkles size={13} style={{ color: accent }} /> Auto-resolve · {AUTO_RESOLVE_LEVELS[level].short}
          </button>
        }
      />

      <div className="flex min-h-0 flex-1">
        {/* List */}
        <div className="flex min-w-0 flex-1 flex-col border-r border-app">
          {/* filters */}
          <div className="space-y-2 border-b border-app px-4 py-2.5">
            <div className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/40 px-2.5 py-1.5">
              <Search size={14} className="text-app-2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…" className="flex-1 bg-transparent text-sm text-app outline-none placeholder:text-app-2" />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {SOURCE_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setSource(s)}
                  className="rounded-md px-2 py-1 text-[11px] font-medium capitalize transition"
                  style={{ background: source === s ? `${accent}1f` : "transparent", color: source === s ? accent : "var(--text-2)" }}
                >
                  {s === "all" ? "All sources" : TASK_SOURCE_LABEL[s]}
                </button>
              ))}
              <span className="mx-1 h-4 w-px bg-app" />
              {(["all", "ready", "clarify"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setQuick(f)}
                  className="rounded-md px-2 py-1 text-[11px] font-medium capitalize transition"
                  style={{ background: quick === f ? `${accent}1f` : "transparent", color: quick === f ? accent : "var(--text-2)" }}
                >
                  {f === "all" ? "All" : f === "ready" ? (aresEnabled ? "Ready for ARES" : "Ready") : "Needs clarification"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto no-scrollbar p-3">
            <AnimatePresence>
              {filtered.map((t, i) => (
                <TaskRow key={t.id} task={t} index={i} />
              ))}
            </AnimatePresence>
            {filtered.length === 0 && <div className="py-16 text-center text-sm text-app-2">No tasks match these filters.</div>}
          </div>
        </div>

        {/* Detail */}
        <div className="hidden w-[460px] shrink-0 lg:block">
          <AnimatePresence mode="wait">
            {selected ? (
              <TaskDetail task={selected} />
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid h-full place-items-center p-8 text-center">
                <div>
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-surface-2 text-app-2"><Inbox size={22} /></div>
                  <p className="mt-3 text-sm text-app-2">
                    {aresEnabled
                      ? "Select a task to refine it, ask follow-ups, and send it to ARES."
                      : "Select a task to refine it and ask follow-ups."}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Modal open={autoOpen} onClose={() => setAutoOpen(false)} accent={accent} icon={<Sparkles size={18} />} title="Auto-Resolve" subtitle="How much of the queue should Hive handle on its own?" width={520}>
        <AutoResolvePanel />
      </Modal>
    </div>
  );
}
